import { asLink } from '@prismicio/client';

import type { RTHeading5Node, RTHeading6Node, RTParagraphNode } from '@prismicio/client';

/**
 * The heuristics that give the production content pages a hierarchy without
 * editing the documents, which are read-only. Pure functions, so they are
 * testable against the shapes the real documents actually have.
 */

/**
 * A paragraph that is nothing but one short bold run is a heading the editor
 * typed as body text — the Privacy and Terms documents are written entirely
 * that way. Promoting it to a real `<h2>` is what gives those pages an outline
 * for screen readers and something scannable on screen (audit #19).
 *
 * The length cap matters: several Terms clauses are a whole bold paragraph, and
 * a 300-character heading is worse than no heading.
 */
export const HEADING_LIKE_MAX_LENGTH = 80;

export function isHeadingLike(node: RTParagraphNode): boolean {
  const text = node.text.trim();

  if (text === '' || text.length > HEADING_LIKE_MAX_LENGTH) {
    return false;
  }

  const [span, ...rest] = node.spans;

  return rest.length === 0 && span?.type === 'strong' && span.start === 0 && span.end >= node.text.trimEnd().length;
}

/** Editors use empty paragraphs (often a single nbsp) as spacers. Drop them. */
export function isBlank(node: RTParagraphNode): boolean {
  return node.text.replace(/[\s ]/g, '') === '';
}

/**
 * A heading whose entire text is one hyperlink. The Resources document is a
 * list of exactly these, which is what lets them render as link cards instead
 * of a wall of blue text (audit #20).
 */
export function wholeNodeLink(node: RTHeading5Node | RTHeading6Node): { href: string; label: string } | null {
  const [span, ...rest] = node.spans;

  if (rest.length > 0 || span?.type !== 'hyperlink' || span.start !== 0 || span.end < node.text.trimEnd().length) {
    return null;
  }

  // Editors sometimes type the URL with a stray leading space.
  const href = asLink(span.data)?.trim();

  // They have also typed the affordance into the copy as a trailing arrow; the
  // card draws its own, so strip it rather than showing two.
  const label = node.text.replace(/[\s ]*[↗➚→»>]+$/u, '').trim();

  return href && label ? { href, label } : null;
}

/** True for a URL that leaves the site, and so needs `target` and `rel`. */
export const isExternalHref = (href: string) => /^(https?:)?\/\//.test(href);
